/**
 * useClickOutside — close a dropdown/popover/modal when the user clicks
 * outside of the referenced element.
 *
 * Replaces the pattern:
 *   useEffect(() => {
 *     const handler = (e: MouseEvent) => {
 *       if (ref.current && !ref.current.contains(e.target as Node)) close();
 *     };
 *     document.addEventListener('mousedown', handler);
 *     return () => document.removeEventListener('mousedown', handler);
 *   }, [isOpen]);
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useClickOutside(ref, () => setIsOpen(false), isOpen);
 */
import { useEffect, type RefObject } from 'react';

export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  onClickOutside: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClickOutside, enabled]);
}
